import { PublicPageShell, PublicPageCta } from "./PublicPageShell";
import { TEMPLATES } from "../canvas/templates";
import { TemplatePreview } from "../canvas/TemplatePreview";
import { cn } from "../cn";
import { useI18n } from "../../i18n";

/**
 * CreatorsPage — MACRO #26 · Increment 3 (`#creators`).
 *
 * Use-cases for creators & brands, anchored to the six real premium Canvas
 * templates. Copy is sourced from the i18n layer (English canonical).
 */
export function CreatorsPage() {
  const { dict, t } = useI18n();
  const useCases = dict.creators.useCases as Record<string, string>;
  return (
    <PublicPageShell
      testId="creators-page"
      index="C · 04"
      eyebrow={t("creators.eyebrow")}
      title={
        <>
          {dict.creators.titleLine1}
          <br />
          <span
            className="bg-clip-text text-transparent"
            style={{ backgroundImage: "linear-gradient(90deg,#FF6B76 0%,#FF4757 55%,#FF3040 100%)" }}
          >
            {dict.creators.titleHighlight}
          </span>
        </>
      }
      intro={t("creators.intro")}
      footer={<PublicPageCta />}
    >
      <div className="flex items-center gap-3 mb-6">
        <span aria-hidden className="h-px w-8 bg-text-muted" />
        <span className="font-mono text-[10.5px] tracking-[0.26em] uppercase text-text-secondary">
          {t("creators.sixTemplates")}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
        {TEMPLATES.map((def) => (
          <article
            key={def.id}
            data-testid={`creators-template-${def.id}`}
            className={cn(
              "group flex flex-col rounded-lg border border-border-soft bg-obsidian/50 overflow-hidden",
              "hover:border-border-accent transition-colors duration-med",
            )}
          >
            <div className="relative aspect-[4/3] w-full border-b border-border-divider overflow-hidden">
              <TemplatePreview def={def} />
              <span
                aria-hidden
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-med"
                style={{ boxShadow: "inset 0 0 44px -10px rgba(45,182,240,0.4)" }}
              />
            </div>
            <div className="flex flex-col flex-1 p-5 gap-2">
              <div className="flex items-baseline justify-between gap-2">
                <h2 className="font-display text-[14px] font-semibold tracking-[0.04em] text-text-primary">
                  {def.name}
                </h2>
                <span className="font-mono text-[9px] tracking-[0.16em] uppercase text-text-muted whitespace-nowrap">
                  {def.useCase}
                </span>
              </div>
              <p className="flex-1 font-display text-[12.5px] leading-[1.55] text-text-secondary">
                {useCases[def.id] ?? def.tagline}
              </p>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {def.tags.map((tag) => (
                  <span
                    key={tag}
                    className="font-mono text-[9px] tracking-[0.12em] uppercase text-text-muted border border-border-soft rounded-sm px-1.5 py-0.5"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </article>
        ))}
      </div>

      <div
        data-testid="creators-how-note"
        className="mt-10 neon-glass p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between"
      >
        <p className="font-display text-[13px] leading-[1.6] text-text-secondary max-w-[640px]">
          {dict.creators.noteBefore}
          <span className="text-text-primary font-semibold">{dict.creators.noteBold}</span>
          {dict.creators.noteAfter}
        </p>
        <a
          data-testid="creators-how-cta"
          href="#how-it-works"
          className="facet-btn h-10 px-5 no-underline text-[11px] font-semibold tracking-[0.2em] justify-center shrink-0"
        >
          {t("creators.howCta")}
        </a>
      </div>

      <div
        data-testid="creators-onboarding-cta"
        className="mt-5 neon-glass p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between border border-border-accent shadow-glow-teal-sm"
      >
        <div className="max-w-[640px]">
          <div className="font-mono text-[9.5px] tracking-[0.22em] uppercase text-neon-teal">
            {t("onboarding.creatorsCta.title")}
          </div>
          <p className="mt-1.5 font-display text-[13px] leading-[1.6] text-text-secondary">
            {t("onboarding.creatorsCta.body")}
          </p>
        </div>
        <a
          data-testid="creators-onboarding-cta-link"
          href="#onboarding"
          className="facet-btn h-10 px-5 no-underline text-[11px] font-semibold tracking-[0.2em] justify-center shrink-0"
        >
          {t("onboarding.creatorsCta.cta")}
        </a>
      </div>
    </PublicPageShell>
  );
}
